import type { TFunction } from "i18next";
import {
  dashboardDataTable as dt,
  type DefinitionTooltipInfo,
  type SortDirection,
} from "src/components/common";
import {
  type MetaAdsAdItem,
  type MetaAdsAdsetItem,
  type MetaAdsCampaignItem,
  type MetaAdsPaginationMeta,
  type MetaAdsPerformanceMetrics,
} from "src/services/MetaAdsDashboardAPI";
import { formatPaidMediaCount } from "../../utils/paidMediaNumberFormat";

export type ActiveOverviewTab = "campaigns" | "adsets" | "ads" | "alerts";

export type CampaignSummary = {
  id: string;
  campaignName: string;
  platform: string;
  objective: null;
  status: null;
  budget: number | null;
  budgetCategory: string | null;
  dailyBudget: number | null;
  budgetRemaining: number | null;
  spend: number | null;
  impressions: number | null;
  clicks: number | null;
  conversions: number | null;
  ctr: number | null;
  cpc: number | null;
  cpa: number | null;
  roas: number | null;
  revenue: number | null;
  adsets: MetaAdsAdsetItem[];
  linkClicks: number | null;
  campaignStartAt: string | null;
  campaignStopAt: string | null;
  campaignCreatedAt: string | null;
};

export type AlertRule = {
  id: "high-cpa" | "low-roas" | "high-frequency";
  metric: "cpa" | "roas" | "frequency";
  operator: ">" | "<";
  threshold: number;
  enabled: boolean;
};
export type CampaignSortKey =
  | "campaignName"
  | "budget"
  | "revenue"
  | "spend"
  | "impressions"
  | "clicks"
  | "ctr"
  | "cpc"
  | "cpa"
  | "roas"
  | "conversions"
  | "linkClicks"
  | "campaignStartAt"
  | "campaignStopAt"
  | "campaignCreatedAt";
export type AdSetSortKey =
  | "name"
  | "budget"
  | "spend"
  | "impressions"
  | "conversions"
  | "ctr"
  | "roas"
  | "linkClicks"
  | "adsetStartAt"
  | "adsetEndAt"
  | "adsetCreatedAt";
export type AdSortKey =
  | "name"
  | "adId"
  | "type"
  | "spend"
  | "impressions"
  | "conversions"
  | "ctr"
  | "cpc"
  | "cpa"
  | "roas"
  | "linkClicks"
  | "createdAtDate"
  | "createdAtTime";


export type LiveAdSetRow = {
  id: string;
  name: string;
  platform: string;
  spend: number;
  impressions: number;
  conversions: number;
  ctr: number | null;
  cpc: number | null;
  cpa: number | null;
  roas: number;
  revenue: number | null;
  budgetCategory: string | null;
  dailyBudget: number | null;
  budgetRemaining: number | null;
  linkClicks: number | null;
  adsetStartAt: string | null;
  adsetEndAt: string | null;
  adsetCreatedAt: string | null;
};
export type LiveAdRow = {
  id: string;
  name: string;
  spend: number;
  impressions: number;
  clicks: number;
  conversions: number;
  ctr: number;
  cpc: number;
  cpa: number;
  roas: number;
  revenue: number | null;
  dailyBudget: number | null;
  budgetRemaining: number | null;
  linkClicks: number | null;
  createdAtDate: string | null;
  createdAtTime: string | null;
};

export const TABLE_PAGE_SIZE = 10;
export const TREND_PAGE_SIZE = 10;
export const SEARCH_DEBOUNCE_MS = 300;

export const EMPTY_PAGINATION_META: MetaAdsPaginationMeta = {
  page: 1,
  page_size: TABLE_PAGE_SIZE,
  total_items: 0,
  total_pages: 1,
};

export const DEFAULT_ALERT_RULES: AlertRule[] = [
  {
    id: "high-cpa",
    metric: "cpa",
    operator: ">",
    threshold: 40,
    enabled: true,
  },
  {
    id: "low-roas",
    metric: "roas",
    operator: "<",
    threshold: 1,
    enabled: true,
  },
  {
    id: "high-frequency",
    metric: "frequency",
    operator: ">",
    threshold: 3,
    enabled: false,
  },
];

/**
 * Column-header help for the Meta tables. `key` is a `*Table.*` label key, so a
 * column's label and its tooltip stay addressed by the same name.
 *
 * `level` selects the ad-set / ad wording for the columns whose definition names
 * the entity ("spent by the ad set…"). Columns that read the same at every level
 * (CTR, ROAS, Link Clicks, Daily Budget) are authored once in the shared
 * campaign bundle and resolved by the fallback below.
 */
export const getMetaColumnTooltip = (
  t: TFunction,
  key: string,
  level?: "adSet" | "ad"
): DefinitionTooltipInfo => {
  const bases = [
    ...(level ? [`metaAdsReport.${level}ColumnTooltips`] : []),
    "metaAdsReport.columnTooltips",
  ];

  for (const base of bases) {
    const description = t(`${base}.${key}.description`, { defaultValue: "" });
    if (description) {
      const formula = t(`${base}.${key}.formula`, { defaultValue: "" });
      return formula ? { description, formula } : { description };
    }
  }

  // Present in neither bundle — fall through to i18next's own missing-key output.
  return { description: t(`metaAdsReport.columnTooltips.${key}.description`) };
};

export const EMPTY_API_VALUE = "-";
export const formatOptionalNumber = (
  value: number | null | undefined,
  formatter: (_x: number) => string = formatPaidMediaCount
) => (value === null || value === undefined ? EMPTY_API_VALUE : formatter(value));

export const getRoasToneClass = (value: number) => {
  if (value >= 4) {
    return dt.metricSuccess;
  }

  if (value >= 2) {
    return dt.metricWarning;
  }

  return dt.metricDanger;
};
export const getAlertValueToneClass = (
  metric: AlertRule["metric"],
  value: number
) => {
  if (metric === "roas") {
    return value >= 4 ? "paid-media-alert-value--success" : "paid-media-alert-value--danger";
  }

  if (metric === "cpa") {
    return value <= 40 ? "paid-media-alert-value--success" : "paid-media-alert-value--danger";
  }

  return value <= 3 ? "paid-media-alert-value--success" : "paid-media-alert-value--danger";
};

export const computeAlertMetricValue = (alert: AlertRule, campaign: CampaignSummary): number => {
  if (alert.metric === "cpa") {
    return campaign.cpa ?? 0;
  }
  if (alert.metric === "roas") {
    return campaign.roas ?? 0;
  }
  if ((campaign.impressions ?? 0) > 0) {
    return (campaign.clicks ?? 0) / Math.max(campaign.conversions ?? 0, 1);
  }
  return 0;
};

export const toFiniteNumber = (value: unknown, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};
export const toNullableFiniteNumber = (value: unknown) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

export const mapPerformanceMetrics = (item: MetaAdsPerformanceMetrics) => ({
  revenue: toNullableFiniteNumber(item.total_revenue),
  spend: toNullableFiniteNumber(item.total_spend),
  impressions: toNullableFiniteNumber(item.impressions),
  clicks: toNullableFiniteNumber(item.clicks),
  conversions: toNullableFiniteNumber(item.purchases),
  ctr: toNullableFiniteNumber(item.ctr),
  cpc: toNullableFiniteNumber(item.cost_per_click),
  cpa: toNullableFiniteNumber(item.cost_per_purchase),
  roas: toNullableFiniteNumber(item.roas),
});

export const mapCampaignApiItem = (item: MetaAdsCampaignItem): CampaignSummary => ({
  // Campaign names can repeat across campaigns, so key rows by the stable id.
  id: item.campaign_id ?? item.campaign_name ?? "campaign",
  campaignName: item.campaign_name ?? EMPTY_API_VALUE,
  platform: item.platform_name?.trim() || EMPTY_API_VALUE,
  objective: null,
  status: null,
  budget: null,
  budgetCategory: item.budget_category ?? null,
  dailyBudget: toNullableFiniteNumber(item.daily_budget),
  budgetRemaining: toNullableFiniteNumber(item.budget_remaining),
  adsets: item.adsets ?? [],
  linkClicks: toNullableFiniteNumber(item.link_clicks),
  campaignStartAt: item.campaign_start_at ?? null,
  campaignStopAt: item.campaign_stop_at ?? null,
  campaignCreatedAt: item.campaign_created_at ?? null,
  ...mapPerformanceMetrics(item),
});

export const mapAdsetApiItem = (item: MetaAdsAdsetItem, index: number): LiveAdSetRow => {
  const metrics = mapPerformanceMetrics(item);
  return {
    id: `${item.adset_id ?? item.adset_name}-${index}`,
    name: item.adset_name ?? `Ad Set ${index + 1}`,
    platform: item.platform_name?.trim() || EMPTY_API_VALUE,
    spend: metrics.spend ?? 0,
    impressions: metrics.impressions ?? 0,
    conversions: metrics.conversions ?? 0,
    ctr: metrics.ctr,
    cpc: metrics.cpc,
    cpa: metrics.cpa,
    roas: metrics.roas ?? 0,
    revenue: metrics.revenue,
    budgetCategory: item.budget_category ?? null,
    dailyBudget: toNullableFiniteNumber(item.daily_budget),
    budgetRemaining: toNullableFiniteNumber(item.budget_remaining),
    linkClicks: toNullableFiniteNumber(item.link_clicks),
    adsetStartAt: item.adset_start_at ?? null,
    adsetEndAt: item.adset_end_at ?? null,
    adsetCreatedAt: item.adset_created_at ?? null,
  };
};

export const mapAdApiItem = (item: MetaAdsAdItem, index: number): LiveAdRow => {
  const metrics = mapPerformanceMetrics(item);
  return {
    id: `${item.ad_id ?? item.ad_name}-${index}`,
    name: item.ad_name ?? `Ad ${index + 1}`,
    spend: metrics.spend ?? 0,
    impressions: metrics.impressions ?? 0,
    clicks: metrics.clicks ?? 0,
    conversions: metrics.conversions ?? 0,
    ctr: metrics.ctr ?? 0,
    cpc: metrics.cpc ?? 0,
    cpa: metrics.cpa ?? 0,
    roas: metrics.roas ?? 0,
    revenue: metrics.revenue,
    dailyBudget: toNullableFiniteNumber(item.daily_budget),
    budgetRemaining: toNullableFiniteNumber(item.budget_remaining),
    linkClicks: toNullableFiniteNumber(item.link_clicks),
    createdAtDate: item.created_at_date ?? null,
    createdAtTime: item.created_at_time ?? null,
  };
};

// Only fields the backend accepts for `sort`. Date/timestamp columns and
// link_clicks are display-only — the API rejects sorting by them.
export const CAMPAIGN_SORT_FIELD: Partial<Record<CampaignSortKey, string>> = {
  campaignName: "campaign_name",
  revenue: "total_revenue",
  spend: "total_spend",
  impressions: "impressions",
  ctr: "ctr",
  cpc: "cost_per_click",
  cpa: "cost_per_purchase",
  roas: "roas",
  conversions: "purchases",
};

// Date/timestamp columns and link_clicks are display-only; the API rejects
// sorting by them, matching the campaigns endpoint's allowed list.
export const ADSET_SORT_FIELD: Partial<Record<AdSetSortKey, string>> = {
  name: "adset_name",
  spend: "total_spend",
  impressions: "impressions",
  conversions: "purchases",
  ctr: "ctr",
  roas: "roas",
};

export const AD_SORT_FIELD: Partial<Record<AdSortKey, string>> = {
  name: "ad_name",
  spend: "total_spend",
  impressions: "impressions",
  conversions: "purchases",
  ctr: "ctr",
  cpc: "cost_per_click",
  cpa: "cost_per_purchase",
  roas: "roas",
};

export const toApiSort = <T extends string>(
  sort: { key: string; direction: SortDirection } | null,
  fieldMap: Partial<Record<string, string>>,
  fallback: T
): T | undefined => {
  if (!sort) {
    return fallback;
  }
  const base = fieldMap[sort.key];
  if (!base) {
    return undefined;
  }
  return `${base}_${sort.direction}` as T;
};
